import { Box, Grid } from "@mui/joy";
import IconButtonCTA from "../../../common/IconButtonCTA";
import { SDelete, SSave, AddIcon } from "../../../../styles/StyledMUI";
import CreateTrainingTemplateModal from "./CreateTrainingTemplateModal";
import TrainingsEmptyState from "../../common/TrainingsEmptyState";
import { TRAININGS_EMPTY_SELECT_TEMPLATE_MESSAGE } from "../../common/trainingsUiCopy";
import TrainingsQualificationSelect from "../../common/TrainingsQualificationSelect";
import TrainingsTemplateDashboardFrame from "../../common/TrainingsTemplateDashboardFrame";
import {
  TRAINING_TEMPLATE_ROW_ACTIONS_WIDTH,
  useTrainingTemplatesEditor,
} from "../hooks/useTrainingTemplatesEditor";
import TrainingTemplateOrganisationFields from "./TrainingTemplateOrganisationFields";
import TrainingTemplateCoursesSection from "./TrainingTemplateCoursesSection";
import TrainingTemplateSdiCard from "./TrainingTemplateSdiCard";
import {
  TrainingTemplateExternalIdentifiersCard,
  TrainingTemplateFinalQualificationCard,
  TrainingTemplateTheoryCard,
} from "./TrainingTemplateQualificationPanels";
import TrainingTemplateCardsBlock from "./TrainingTemplateCardsBlock";
import TrainingTemplateMailsCard from "./TrainingTemplateMailsCard";
import TrainingTemplatesImportOverlay from "./TrainingTemplatesImportOverlay";

export default function TrainingTemplateEditor() {
  const rowActionsWidth = TRAINING_TEMPLATE_ROW_ACTIONS_WIDTH;
  const {
    templates,
    selectedTemplate,
    isLoading,
    newCardName,
    setNewCardName,
    courses,
    setCourses,
    isCoursesLoading,
    newCourseName,
    setNewCourseName,
    newCourseFile,
    setNewCourseFile,
    addingCourse,
    deletingCourseId,
    replacingCourseId,
    savingCourseId,
    editingCourseId,
    setEditingCourseId,
    isUploading,
    isCreateOpen,
    setIsCreateOpen,
    extCredits,
    extQualifications,
    showLocalDestinationQualification,
    showExternalIdentifiers,
    sortedLocalQualifications,
    mailTemplates,
    canEdit,
    inputRefs,
    handleChangeTemplate,
    handleFieldChange,
    handleMailTemplateChange,
    handleFieldChangeWithFocus,
    handleAddCard,
    handleRemoveCard,
    handleSaveTemplate,
    handleDeleteTemplate,
    handleOpenCreate,
    handleSubmitCreate,
    handleAddCourse,
    handleDeleteCourse,
    handleReplaceCourse,
    handleSaveCourseName,
    handleUploadTemplateFile,
  } = useTrainingTemplatesEditor();

  return (
    <>
      <TrainingsTemplateDashboardFrame
        isLoading={isLoading}
        headerLeft={
          <TrainingsQualificationSelect
            templates={templates ?? []}
            value={selectedTemplate?.ID.toString() ?? ""}
            onChange={handleChangeTemplate}
          />
        }
        headerRight={
          <>
            {selectedTemplate ? (
              <IconButtonCTA
                ctaVariant="success"
                aria-label="Sauvegarder le template"
                onClick={handleSaveTemplate}
                disabled={!canEdit}
              >
                <SSave />
              </IconButtonCTA>
            ) : null}
            {selectedTemplate ? (
              <IconButtonCTA
                ctaVariant="danger"
                aria-label="Supprimer le template"
                onClick={handleDeleteTemplate}
                disabled={!canEdit}
              >
                <SDelete />
              </IconButtonCTA>
            ) : null}
            {canEdit ? (
              <IconButtonCTA
                ctaVariant="accent"
                aria-label="Créer un template"
                onClick={handleOpenCreate}
              >
                <AddIcon />
              </IconButtonCTA>
            ) : null}
          </>
        }
      >
        {selectedTemplate ? (
          <Box sx={{ width: "100%" }}>
            <Grid container spacing={2} direction="column">
              <TrainingTemplateOrganisationFields
                selected={selectedTemplate}
                inputRefs={inputRefs}
                onFieldWithFocus={handleFieldChangeWithFocus}
              />

              <TrainingTemplateCoursesSection
                actionsWidth={rowActionsWidth}
                isCoursesLoading={isCoursesLoading}
                courses={courses}
                canEdit={canEdit}
                editingCourseId={editingCourseId}
                savingCourseId={savingCourseId}
                deletingCourseId={deletingCourseId}
                replacingCourseId={replacingCourseId}
                addingCourse={addingCourse}
                newCourseName={newCourseName}
                newCourseFile={newCourseFile}
                onCourseNameDraft={(courseId, name) => {
                  setCourses((previous) =>
                    (previous ?? []).map((course) =>
                      course.ID === courseId ? { ...course, Name: name } : course,
                    ),
                  );
                }}
                onEditingCourse={setEditingCourseId}
                onSaveCourseName={handleSaveCourseName}
                onOpenCourseUrl={(url) => window.open(url, "_blank")}
                onReplaceCourseFile={handleReplaceCourse}
                onDeleteCourse={handleDeleteCourse}
                onNewCourseNameChange={setNewCourseName}
                onNewCourseFileChange={setNewCourseFile}
                onAddCourse={handleAddCourse}
              />

              <TrainingTemplateSdiCard
                actionsWidth={rowActionsWidth}
                selected={selectedTemplate}
                canEdit={canEdit}
                isUploading={isUploading}
                onIsSdiChange={(value) => handleFieldChange("IsSDI", value)}
                onUploadMedical={handleUploadTemplateFile}
              />

              {showLocalDestinationQualification ? (
                <TrainingTemplateFinalQualificationCard
                  selected={selectedTemplate}
                  sortedQualifications={sortedLocalQualifications}
                  canEdit={canEdit}
                  onDestinationQualificationChange={(qualificationId) =>
                    handleFieldChange("DestinationQualificationID", qualificationId)
                  }
                />
              ) : null}

              {showExternalIdentifiers ? (
                <TrainingTemplateExternalIdentifiersCard
                  selected={selectedTemplate}
                  inputRefs={inputRefs}
                  onFieldWithFocus={handleFieldChangeWithFocus}
                />
              ) : null}

              <TrainingTemplateTheoryCard
                selected={selectedTemplate}
                onNeedsTheory={(value) => handleFieldChange("NeedsTheory", value)}
                onNeedsRifap={(value) => handleFieldChange("NeedsRifap", value)}
              />

              <TrainingTemplateMailsCard
                selected={selectedTemplate}
                canEdit={canEdit}
                mailTemplates={mailTemplates}
                onMailTemplateChange={handleMailTemplateChange}
              />

              <TrainingTemplateCardsBlock
                selected={selectedTemplate}
                newCardName={newCardName}
                onNewCardNameChange={setNewCardName}
                onAddCard={handleAddCard}
                onRemoveCard={handleRemoveCard}
                canEdit={canEdit}
              />
            </Grid>
          </Box>
        ) : (
          <TrainingsEmptyState message={TRAININGS_EMPTY_SELECT_TEMPLATE_MESSAGE} />
        )}
      </TrainingsTemplateDashboardFrame>

      <TrainingTemplatesImportOverlay open={isUploading} />

      <CreateTrainingTemplateModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreate={handleSubmitCreate}
        credits={extCredits}
        qualifications={extQualifications}
        isSubmitting={isLoading}
      />
    </>
  );
}
